/**
 * usePostcodeLookup Hook
 *
 * React hook for country-specific postcode validation and city lookup.
 * Features:
 * - Debounced API calls (300-400ms)
 * - Format validation
 * - Automatic city lookup
 * - Error handling with graceful degradation
 * - Manual override support
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { PostcodeLookupState } from '@/types/postcode';
import { validatePostcode, lookupPostcode } from '@/services/postcodeApi';

interface UsePostcodeLookupOptions {
  /** Country for postcode validation */
  country: string;
  /** Postcode value to validate and lookup */
  postcode: string;
  /** Debounce delay in milliseconds (default: 350ms) */
  debounceMs?: number;
  /** Callback when city is found */
  onCityFound?: (city: string) => void;
  /** Whether to automatically perform lookup (default: true) */
  autoLookup?: boolean;
}

/**
 * Hook for postcode validation and city lookup
 *
 * @example
 * ```tsx
 * const { city, isLoading, error, isValid, reset } = usePostcodeLookup({
 *   country: 'United Kingdom',
 *   postcode: 'SW1A 1AA',
 *   onCityFound: (city) => setCity(city)
 * });
 * ```
 */
export function usePostcodeLookup({
  country,
  postcode,
  debounceMs = 350,
  onCityFound,
  autoLookup = true,
}: UsePostcodeLookupOptions): PostcodeLookupState {
  const [city, setCity] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean | null>(null);

  // Track the last looked up postcode to prevent duplicate lookups
  const lastLookedUpPostcode = useRef<string>('');

  /**
   * Reset all state
   */
  const reset = useCallback(() => {
    setCity(null);
    setIsLoading(false);
    setError(null);
    setIsValid(null);
    lastLookedUpPostcode.current = '';
  }, []);

  /**
   * Perform postcode lookup
   */
  const performLookup = useCallback(
    async (postcodeValue: string) => {
      // Don't lookup if empty
      if (!postcodeValue.trim()) {
        reset();
        return;
      }

      // Skip if we've already looked up this exact postcode
      const normalizedPostcode = postcodeValue.trim().toUpperCase();
      if (lastLookedUpPostcode.current === normalizedPostcode) {
        return;
      }

      lastLookedUpPostcode.current = normalizedPostcode;
      setIsLoading(true);
      setError(null);

      try {
        // Step 1: Validate format
        const validation = validatePostcode(postcodeValue, country);
        setIsValid(validation.isValid);

        if (!validation.isValid) {
          setIsLoading(false);
          setError('Invalid postcode format');
          return;
        }

        // Step 2: Lookup city
        const result = await lookupPostcode(postcodeValue, country);

        setCity(result.city);
        setError(null);

        // Notify parent component
        if (onCityFound) {
          onCityFound(result.city);
        }
      } catch (err) {
        console.error('Postcode lookup error:', err);

        // Set user-friendly error messages
        if (err instanceof Error) {
          if (err.message.includes('not supported')) {
            setError('Postcode lookup not available for this country');
          } else if (err.message.includes('not found')) {
            setError("We couldn't verify this postcode — please enter your city manually.");
          } else if (err.message.includes('Network error')) {
            setError('Network error — please check your connection and try again.');
          } else {
            setError("We couldn't verify this postcode — please enter your city manually.");
          }
        } else {
          setError("We couldn't verify this postcode — please enter your city manually.");
        }

        setCity(null);
      } finally {
        setIsLoading(false);
      }
    },
    [country, onCityFound, reset]
  );

  /**
   * Effect: Debounced postcode lookup
   */
  useEffect(() => {
    if (!autoLookup) {
      return;
    }

    // Reset state if postcode is empty
    if (!postcode.trim()) {
      reset();
      return;
    }

    // Debounce the lookup
    const timer = setTimeout(() => {
      performLookup(postcode);
    }, debounceMs);

    return () => {
      clearTimeout(timer);
    };
  }, [postcode, debounceMs, autoLookup, performLookup, reset]);

  return {
    city,
    isLoading,
    error,
    isValid,
    reset,
  };
}
